const MONARCH_RULE_TOKEN_PATTERN =
  /\{\{([a-zA-Z][a-zA-Z0-9_-]*)(?:\.([a-zA-Z_][a-zA-Z0-9_.]*))?\}\}/g;
const ARRAY_INDEX_PATTERN = /^(0|[1-9]\d*)$/;
const BLOCKED_PATH_SEGMENTS = new Set([
  "__proto__",
  "constructor",
  "prototype",
]);
const NO_CHANGE = Symbol("NO_CHANGE");

type MonarchRuleValues = Record<string, unknown>;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

// Rule values also reach plain-text sinks (aria-labels, analytics, URLs), so
// only escape what can open a tag or break out of a double-quoted attribute.
const escapeHtml = (value: string): string =>
  value
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");

const isUnavailableRuleValue = (value: unknown): boolean =>
  value === false ||
  (isRecord(value) &&
    (value.return_status === false || value.return_status === "false"));

const getValueAtPath = (value: unknown, path: string | undefined): unknown => {
  if (!path) return value;

  return path.split(".").reduce<unknown>((currentValue, segment) => {
    if (
      BLOCKED_PATH_SEGMENTS.has(segment) ||
      (!isRecord(currentValue) && !Array.isArray(currentValue))
    ) {
      return undefined;
    }

    if (Array.isArray(currentValue)) {
      return ARRAY_INDEX_PATTERN.test(segment)
        ? currentValue[Number(segment)]
        : undefined;
    }

    return Object.prototype.hasOwnProperty.call(currentValue, segment)
      ? currentValue[segment]
      : undefined;
  }, value);
};

const setExistingStringAtPath = (
  value: unknown,
  path: string[],
  replacement: string
): unknown | typeof NO_CHANGE => {
  if (path.length === 0) {
    return typeof value === "string" ? replacement : NO_CHANGE;
  }

  const [segment, ...remainingPath] = path;
  if (BLOCKED_PATH_SEGMENTS.has(segment)) return NO_CHANGE;

  if (Array.isArray(value)) {
    if (!ARRAY_INDEX_PATTERN.test(segment)) return NO_CHANGE;

    const index = Number(segment);
    if (index >= value.length) return NO_CHANGE;

    const updatedChild = setExistingStringAtPath(
      value[index],
      remainingPath,
      replacement
    );
    if (updatedChild === NO_CHANGE) return NO_CHANGE;

    const updatedArray = [...value];
    updatedArray[index] = updatedChild;
    return updatedArray;
  }

  if (
    !isRecord(value) ||
    !Object.prototype.hasOwnProperty.call(value, segment)
  ) {
    return NO_CHANGE;
  }

  const updatedChild = setExistingStringAtPath(
    value[segment],
    remainingPath,
    replacement
  );
  if (updatedChild === NO_CHANGE) return NO_CHANGE;

  return {
    ...value,
    [segment]: updatedChild,
  };
};

/**
 * Resolves namespaced tokens such as `{{cartRule.offer.price}}` directly
 * against Monarch global-rule custom IDs and their returned values.
 */
export const interpolateMonarchRuleTokens = (
  template: string,
  globalRules: MonarchRuleValues
): string | null => {
  let hasToken = false;
  let hasUnresolvedToken = false;

  const interpolated = template.replace(
    MONARCH_RULE_TOKEN_PATTERN,
    (_token, ruleCustomId: string, valuePath: string | undefined) => {
      hasToken = true;
      const ruleValue = globalRules[ruleCustomId];

      if (isUnavailableRuleValue(ruleValue)) {
        hasUnresolvedToken = true;
        return "";
      }

      const replacement = getValueAtPath(ruleValue, valuePath);
      if (
        typeof replacement !== "string" &&
        typeof replacement !== "number" &&
        typeof replacement !== "boolean"
      ) {
        hasUnresolvedToken = true;
        return "";
      }

      return escapeHtml(String(replacement));
    }
  );

  return hasToken && !hasUnresolvedToken ? interpolated : null;
};

/**
 * Applies authored `ruleOverrides` to existing string fields. Each entry
 * uses `{ target, template }`; unresolved templates leave the target's
 * authored fallback value unchanged.
 */
export const applyMonarchRuleOverrides = <T extends Record<string, unknown>>(
  data: T,
  globalRules: MonarchRuleValues
): T => {
  if (!Array.isArray(data.ruleOverrides)) return data;

  return data.ruleOverrides.reduce<T>((resolvedData, override) => {
    if (
      !isRecord(override) ||
      typeof override.target !== "string" ||
      typeof override.template !== "string"
    ) {
      return resolvedData;
    }

    const targetPath = override.target.split(".");
    if (
      targetPath.some((segment) => segment.length === 0) ||
      override.template.trim() === ""
    ) {
      return resolvedData;
    }

    const interpolated = interpolateMonarchRuleTokens(
      override.template,
      globalRules
    );
    if (interpolated === null) return resolvedData;

    const updatedData = setExistingStringAtPath(
      resolvedData,
      targetPath,
      interpolated
    );

    return updatedData === NO_CHANGE ? resolvedData : (updatedData as T);
  }, data);
};
